// Espelha exactamente a validação backend `_validate_mbway_phone`
// em `services/native_payment.py` para evitar drift. O backend é o
// gate-keeper definitivo (caso #70395 — Cristina, ENC 90€), mas
// validar no frontend dá feedback imediato e poupa round-trip.
//
// Validação por país conhecido (PT/ES/FR/UK) com regex específica
// + fallback genérico (4-15 dígitos E.164).
//
// Aceita formatos:
//   - "351 912345678" (indicativo + espaço + número)
//   - "+351 912345678" (com +)
//   - "351912345678" (junto — assume primeiros 2-4 dígitos como indicativo)
//   - "912345678" (PT implícito — frontend assume 351)

const KNOWN_INDICATIVES = {
  "351": /^9[0-9]{8}$/,        // Portugal: 9 dígitos a começar por 9
  "34":  /^[67][0-9]{8}$/,     // Espanha: 9 dígitos a começar por 6 ou 7
  "33":  /^[67][0-9]{8}$/,     // França: 9 dígitos
  "44":  /^7[0-9]{9}$/,        // Reino Unido: 10 dígitos a começar por 7
};

/**
 * Divide `raw` em `{indicativo, telemovel}`. Se o utilizador escreveu apenas
 * 9 dígitos sem indicativo, assume PT (351).
 * @param {string} raw
 * @returns {{indicativo: string, telemovel: string}}
 */
function splitPhoneInput(raw) {
  const s = String(raw || "").trim().replace(/^\+/, "");
  // Formato "351 912345678" ou "351-912345678"
  const m = s.match(/^(\d{1,4})[\s\-](\d+)$/);
  if (m) return { indicativo: m[1], telemovel: m[2].replace(/[\s\-]/g, "") };
  // Sem separador — todos os dígitos juntos
  const digits = s.replace(/\D/g, "");
  // Se começa por "351" e tem 12 dígitos no total
  if (digits.startsWith("351") && digits.length === 12) {
    return { indicativo: "351", telemovel: digits.slice(3) };
  }
  if (digits.startsWith("34") && digits.length === 11) {
    return { indicativo: "34", telemovel: digits.slice(2) };
  }
  if (digits.startsWith("33") && digits.length === 11) {
    return { indicativo: "33", telemovel: digits.slice(2) };
  }
  if (digits.startsWith("44") && digits.length === 12) {
    return { indicativo: "44", telemovel: digits.slice(2) };
  }
  // 9 dígitos isolados → assume PT
  if (digits.length === 9) {
    return { indicativo: "351", telemovel: digits };
  }
  return { indicativo: "", telemovel: digits };
}

/**
 * Valida um telefone MBWay. Devolve `null` se válido, ou string com mensagem
 * de erro user-friendly em PT.
 * @param {string} rawPhone
 * @returns {string|null}
 */
export function validateMbwayPhone(rawPhone) {
  const phone = String(rawPhone || "").trim();
  if (!phone) return "Indique o número de telemóvel MBWay.";
  const { indicativo, telemovel } = splitPhoneInput(phone);
  if (!indicativo || !telemovel) {
    return "Telemóvel MBWay incompleto (indicativo + número).";
  }
  if (!/^\d+$/.test(indicativo) || !/^\d+$/.test(telemovel)) {
    return "Telemóvel MBWay contém caracteres inválidos.";
  }
  if (indicativo.length < 1 || indicativo.length > 4) {
    return `Indicativo internacional inválido: '${indicativo}'.`;
  }
  const pattern = KNOWN_INDICATIVES[indicativo];
  if (pattern) {
    if (!pattern.test(telemovel)) {
      if (indicativo === "351") {
        return "Telemóvel português inválido para MBWay: deve ter 9 dígitos a começar por 9.";
      }
      return `Telemóvel inválido para o indicativo +${indicativo}.`;
    }
    return null;
  }
  // Fallback genérico
  if (telemovel.length < 4 || telemovel.length > 15) {
    return "Número de telemóvel com comprimento inválido (4-15 dígitos).";
  }
  return null;
}

/**
 * Util: formata um telemóvel para o formato que o backend espera:
 * `"<indicativo> <telemovel>"` (ex: `"351 912345678"`).
 * Devolve string vazia se input não é parseable.
 * @param {string} rawPhone
 * @returns {string}
 */
export function formatMbwayPhoneForBackend(rawPhone) {
  const { indicativo, telemovel } = splitPhoneInput(rawPhone);
  if (!indicativo || !telemovel) return "";
  return `${indicativo} ${telemovel}`;
}
